import React, { useState, useEffect } from "react";
import { Card, Row, Col, message, Modal, Tag } from "antd";
import TableComponent from "../../../components/table/table";
import { get, put, del } from "../../../Api/http/httpClient";
import AgentsCompanyModal from "../../../components/modals/AgentsCompanyModal";

const { confirm } = Modal;

interface AgentsCompanyDataProps {
  user_data: any;
}

const AgentsCompanyData: React.FC<AgentsCompanyDataProps> = ({ user_data }) => {
  const [data, setData] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortColumn, setSortColumn] = useState("bot_id");
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState<any>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const headers_table = {
    bot_id: "ID del Bot",
    dsid: "ID del Conjunto de Datos",
    kbid: "ID de Base de Conocimientos",
    supervisor: "Supervisor",
    multi_agents: "Agentes",
    agent_enabled: "Estado"
  };

  const getAgentsCompanyData = async () => {
    setLoading(true);
    try {
      const res = await get(
        `agents_company?page=${page}&page_size=${itemsPerPage}&search=${search}&sort_by=${sortColumn}&order_by=${sortOrder}`
      );

      if (res.answer && res.data) {
        const formattedAgentItems = res.data.map((agent: any) => ({
          bot_id: agent.bot_id || "",
          dsid: agent.dsid || "",
          kbid: agent.kbid || "",
          supervisor: agent.supervisor?.agentName || "Sin supervisor",
          multi_agents: agent.multi_agents?.length || 0,
          agent_enabled: agent.agent_enabled,
          raw_data: agent
        }));
        
        setData(formattedAgentItems);
        setTotalPages(Math.ceil(res.total_items / itemsPerPage));
      } else {
        message.error("No se encontraron agentes.");
      }
    } catch (error) {
      message.error("Error al obtener los datos de agentes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAgentsCompanyData();
  }, [page, itemsPerPage, search, sortOrder, sortColumn]);

  const handleDelete = async (id: string) => {
    confirm({
      title: '¿Estás seguro de eliminar este agente?',
      content: 'Esta acción no se puede deshacer.',
      okText: 'Sí, eliminar',
      okType: 'danger',
      cancelText: 'No, cancelar',
      onOk: async () => {
        setDeletingId(id);
        try {
          const res = await del(`agents_company/${id}`);
          if (res && res.answer) {
            message.success('Agente eliminado correctamente');
            getAgentsCompanyData();
          } else {
            message.error('Error al eliminar el agente');
          }
        } catch (error) {
          message.error('Error al eliminar el agente');
        } finally {
          setDeletingId(null);
        }
      }
    });
  };

  const setActionHandler = (action: { type: string; data?: any }) => {
    if (action.type === "edit" && action.data?.raw_data) {
      if (!action.data.raw_data.agent_enabled) {
        message.warning("No se puede editar un agente deshabilitado");
        return;
      }
      setFormData(action.data.raw_data);
      handleOpenModal();
    } else if (action.type === "reload") {
      getAgentsCompanyData();
    } else if (action.type === "delete" && action.data?.raw_data) {
      handleDelete(action.data.raw_data.bot_id);
    }
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setFormData(null);
  };

  const handleChange = (agentId: string, value: string, isSupervisor?: boolean) => {
    if (!formData) return;
    
    setFormData((prevData: any) => {
      const newData = { ...prevData };
      
      if (isSupervisor) {
        if (newData.supervisor) {
          newData.supervisor = {
            ...newData.supervisor,
            instruction: value
          };
        }
      } else {
        const agentIndex = newData.multi_agents.findIndex((agent: any) => 
          agent.agent_id === agentId
        );
        
        if (agentIndex !== -1) {
          newData.multi_agents[agentIndex] = {
            ...newData.multi_agents[agentIndex],
            instruction: value
          };
        }
      }
      
      return newData;
    });
  };

  const handleUpdate = async (updatedData: any) => {
    if (!updatedData) {
      message.error("No hay datos para actualizar.");
      return;
    }

    setModalLoading(true);
    try {
      const res = await put("agents_company", updatedData);
      
      if (res && res.answer) {
        message.success("Instrucciones actualizadas correctamente.");
        handleCloseModal();
        getAgentsCompanyData();
      } else {
        message.error("Error al actualizar las instrucciones.");
      }
    } catch (error) {
      message.error("Error al actualizar las instrucciones.");
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <Row justify="center" style={{ marginTop: "20px" }}>
      <Col span={23} style={{ paddingLeft: "20px", paddingRight: "20px" }}>
        <Card bordered={false} style={{ borderRadius: "10px", boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", padding: "0px" }}>
          <TableComponent
            data={data}
            headers_table={headers_table}
            loading={loading}
            setSearch={setSearch}
            setAction={setActionHandler}
            totalPages={totalPages}
            setPage={setPage}
            page={page}
            setItemsPerPage={setItemsPerPage}
            itemsPerPage={itemsPerPage}
            setSortOrder={setSortOrder}
            setSortColumn={setSortColumn}
            deleting={deletingId}
            hideCreateButton={true}
            hideDeleteButton={true}
            customRenderers={{
              agent_enabled: (value: boolean) => (
                <Tag color={value ? "success" : "error"}>
                  {value ? "Habilitado" : "Deshabilitado"}
                </Tag>
              )
            }}
          />
        </Card>
      </Col>

      {formData && (
        <AgentsCompanyModal
          open={modalOpen}
          onClose={handleCloseModal}
          formData={formData}
          handleChange={handleChange}
          handleUpdate={handleUpdate}
          loading={modalLoading}
        />
      )}
    </Row>
  );
};

export default AgentsCompanyData; 